import {debounceTime} from 'rxjs/operators';
import {Component, OnInit, Input, OnChanges, OnDestroy} from '@angular/core';
import {WizardWidgetComponent} from '../wizard-widget/wizard-widget.component';
import {RateService} from '../../../services';
import {LCA_ORIGINATOR_PARTY_ROLE_ID} from '../../../_global/wiz.constants';
import {DataHandlerService} from '../../../services/data-handler.service';

@Component({
  selector: 'app-rate',
  templateUrl: './rate.component.html',
  standalone: true
})
export class RateComponent extends WizardWidgetComponent implements OnInit, OnChanges, OnDestroy {
  @Input() type: String = 'motor-vehicle'; // set as motor-vehicle by default
  @Input() originator: any;
  @Input() baseform: any;
  @Input() bidCompliance = false;
  public curParams = '';
  public subscriptions = [];
  public dialDownDisabled = false;
  public realEstateCountWithoutNas = 0;

  constructor(private rateService: RateService,
              public dataHandlerService: DataHandlerService) {
    super(dataHandlerService);
  }

  ngOnChanges(changes) {
    if (changes.originator && changes.originator.currentValue) {
      if (changes.originator.currentValue.id === LCA_ORIGINATOR_PARTY_ROLE_ID) {
        this.formGroup.patchValue({channel: 'retail'});
      } else {
        this.formGroup.patchValue({channel: 'third-party'});
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  async ngOnInit() {
    super.ngOnInit();

    this.dataHandlerService.getAssetChange()
      .subscribe(async (data: any) => {
        const realEstateCountWithoutNas = await this.calculateHomeOwnership();
        this.dataHandlerService.sendRealEstateCountWithoutNas(realEstateCountWithoutNas);
      });

    this.dataHandlerService.getDialDownDisabledFlag().subscribe(async (result: any) => {
      this.dialDownDisabled = result;

      const currentParams = this.formGroup.value;
      currentParams.realEstateCountWithoutNas = await this.calculateHomeOwnership();
      // initialize by retrieving at the start
      const hasOutput = this.formGroup.value.output && Object.keys(this.formGroup.value.output).length > 0;
      if (hasOutput) {
        this.rateService.setRateData(this.formGroup.value.output);
      } else if (!hasOutput && currentParams && !this.dialDownDisabled) {
        if (this.bidCompliance) {
          currentParams.bidCompliance = true;
        }

        this.rateService.getDialDownRates(this.type, currentParams).subscribe((res) => {
          this.dataHandlerService.sendDialDownData(res);
        });
      }
    });

    // if any of the rate parameters change ---
    this.rateService
      .rateParameters
      .subscribe((val) => {
        const fgVal = this.formGroup.value;
        let newVal = fgVal;
        if (val) {
          newVal = Object.assign({}, fgVal, val);
          this.formGroup.patchValue(newVal);
        }
      });

    this.subscriptions.push(this.formGroup.valueChanges.pipe(debounceTime(500)).subscribe(async (val) => {
      if (JSON.stringify(val) === this.curParams) {
        return;
      } // make sure there are updates before calling dial down endpoint
      this.curParams = JSON.stringify(val);
      if (!this.dialDownDisabled) {

      const patchOutput = (this.formGroup.get('output')) ? true : false;
      this.getDialDownRates(this.type, val, patchOutput);
      }
    }));

    this.dataHandlerService.getIsHomeOwner().subscribe((isHomeowner) => {
      this.formGroup.get('isHomeowner').patchValue(isHomeowner);
      const patchOutput = (this.formGroup.get('output')) ? true : false;
      const data = this.formGroup.value;
      this.getDialDownRates(this.type, data, patchOutput);
    });
  }

  async getDialDownRates(type, data: any, patchOutput = false) {
    if (this.bidCompliance) {
      data.bidCompliance = true;
    }

    data.realEstateCountWithoutNas = await this.calculateHomeOwnership();
    this.dataHandlerService.sendRealEstateCountWithoutNas(data.realEstateCountWithoutNas);
    data.classType = this.dataHandlerService.getVehicleTypeValue();

    this.rateService.getDialDownRates(type, data).subscribe((res) => {
      this.dataHandlerService.sendDialDownData(res);
      if (patchOutput) {
        this.formGroup.get('output').patchValue(res, {emitEvent: false});
      }
    });
  }

  calculateHomeOwnership(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const rawValues = this.baseform.getRawValue();
      let assetData: any[];
      let investmentData: any[];
      let homeAssetCount = 0;
      let investmentCount = 0;

      Object.keys(rawValues).forEach((key: string, index: number) => {
        if (rawValues[key]['app-assets-details']) {
          assetData = rawValues[key]['app-assets-details'].assetsList;
          const homes: any[] = assetData.filter((x: any) => x.type === 'home');
          if (homes.length > 0) {
            homes.forEach((home: any, assetIndex: number) => {
              const nonNasOwnership = home.parties.find((x: any) => !x.nonApplicant && x.ownership > 0);

              if (nonNasOwnership) {
                homeAssetCount = homeAssetCount + 1;
              }
            });
          }
        }

        if (rawValues[key]['app-investment-income']) {
          investmentData = rawValues[key]['app-investment-income'].investmentList;
          if (investmentData.length > 0) {
            investmentData.forEach((investment: any, investmentIndex: number) => {
              const nonNasOwnership = investment.parties.find((x: any) => !x.nonApplicant && x.ownership > 0);
              if (nonNasOwnership) {
                investmentCount = investmentCount + 1;
              }
            });
          }
        }

        if (Object.keys(rawValues).length === (index + 1)) {
          resolve(homeAssetCount + investmentCount);
        }
      });
    });
  }
}
